import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, fmtMoney } from "@/lib/api";
import {
  Brain,
  Camera,
  ArrowsClockwise,
  Sparkle,
  Check,
  Warning,
} from "@phosphor-icons/react";

const CONFIDENCE_LABEL = {
  high: "High confidence",
  medium: "Medium confidence",
  low: "Low confidence",
  unknown: "Insufficient data",
};

const CONFIDENCE_COLOR = {
  high: "bg-[#1A3622] text-[#F9F8F6]",
  medium: "bg-[#9C5012] text-[#F9F8F6]",
  low: "bg-[#6B1718] text-[#F9F8F6]",
  unknown: "bg-[#5C5F58] text-[#F9F8F6]",
};

export default function LeoPhotoAssist({ onDraftReady }) {
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null); // { observation, draft, fmv_confidence, ... , file }
  const [preview, setPreview] = useState(null); // local URL for the chosen image
  const [refining, setRefining] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setDraft(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setDraft(null);
    setPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/assets/leo-photo-draft", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setDraft(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Leo couldn't read this one — try a clearer photo.");
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const runCompsSearch = async () => {
    if (!draft?.suggested_comps_query) return;
    setRefining(true);
    try {
      const r = await api.post("/assets/estimate-value", { description: draft.suggested_comps_query });
      setDraft((prev) => ({
        ...prev,
        draft: {
          ...prev.draft,
          fmv: r.data.fmv ?? prev.draft.fmv,
          rcv: r.data.rcv ?? prev.draft.rcv,
        },
        fmv_confidence: r.data.confidence || "medium",
        fmv_reasoning: r.data.reasoning || prev.fmv_reasoning,
        comps: r.data.comps || [],
      }));
      toast.success("Leo refined the valuation with market comps.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Comps search failed.");
    } finally {
      setRefining(false);
    }
  };

  const acceptDraft = () => {
    if (!draft) return;
    const d = draft.draft || {};
    const photoPath = draft.file?.storage_path;
    onDraftReady?.({
      name: d.name || "",
      category: d.category || "other",
      brand: d.brand || "",
      model: d.model || "",
      serial: "",
      condition: d.condition || "",
      location: d.location || "",
      provenance: d.provenance || "",
      notes: [
        d.notes,
        d.estimated_dimensions ? `Dimensions (est.): ${d.estimated_dimensions}` : null,
        draft.fmv_reasoning ? `Leo's valuation note: ${draft.fmv_reasoning}` : null,
      ].filter(Boolean).join("\n\n"),
      fmv: d.fmv ?? "",
      rcv: d.rcv ?? "",
      photo_paths: photoPath ? [photoPath] : [],
    });
    toast.success("Draft loaded. Review and save below.");
  };

  return (
    <section data-testid="leo-photo-assist" className="space-y-6">
      <header className="flex items-start gap-3">
        <div className="w-10 h-10 bg-[#1A3622] flex items-center justify-center border border-[#0F2818] shrink-0">
          <Brain size={18} weight="regular" color="#F9F8F6" />
        </div>
        <div>
          <h2 className="font-serif-display text-2xl text-[#0A192F] leading-tight">Show Leo the item.</h2>
          <p className="text-sm text-[#5C5F58] mt-1 max-w-xl">
            Upload one photograph of the whole item. Leo will draft the record —
            category, description, dimensions, condition, and a fair-market estimate
            with his confidence and reasoning. You always have final say.
          </p>
        </div>
      </header>

      {/* Upload zone */}
      {!preview && !draft && (
        <label
          htmlFor="leo-photo-input"
          data-testid="leo-photo-dropzone"
          className="block border-2 border-dashed border-[#C9C4B8] bg-[#F9F8F6] hover:border-[#1A3622] cursor-pointer transition-colors p-12 text-center"
        >
          <Camera size={32} weight="thin" className="mx-auto text-[#5C5F58] mb-3" />
          <p className="text-sm text-[#0A192F] font-medium">Click to choose a photo</p>
          <p className="text-xs text-[#5C5F58] mt-1">
            JPEG or PNG. One clear shot of the whole item works best — daylight, plain background.
          </p>
          <input
            id="leo-photo-input"
            ref={inputRef}
            type="file"
            accept="image/*"
            className="sr-only"
            data-testid="leo-photo-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </label>
      )}

      {/* Loading state */}
      {busy && (
        <div className="border border-[#E0DDD6] bg-white p-6 flex items-start gap-4" data-testid="leo-photo-busy">
          {preview && (
            <img
              src={preview}
              alt="Uploaded item"
              className="w-32 h-32 object-cover border border-[#E0DDD6] shrink-0"
            />
          )}
          <div className="flex-1">
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1 flex items-center gap-2">
              <ArrowsClockwise size={12} className="animate-spin" />
              Leo is taking a careful look…
            </div>
            <div className="text-xs text-[#5C5F58] italic">
              Reading the item, drafting category, materials, dimensions, and a defensible value.
            </div>
          </div>
        </div>
      )}

      {/* Draft surface */}
      {draft && !busy && (
        <div className="space-y-4" data-testid="leo-photo-draft">
          <div className="border border-[#E0DDD6] bg-white p-5 flex items-start gap-4">
            {preview && (
              <img
                src={preview}
                alt="Uploaded item"
                className="w-32 h-32 object-cover border border-[#E0DDD6] shrink-0"
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1">
                Leo
              </div>
              <p className="text-sm text-[#0A192F] italic" data-testid="leo-observation">
                {draft.observation || "Drafted from your photo."}
              </p>
            </div>
          </div>

          {/* Field draft */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="leo-fields">
            <Field label="Name" value={draft.draft?.name} />
            <Field label="Category" value={prettyCategory(draft.draft?.category)} />
            <Field label="Brand" value={draft.draft?.brand} mono />
            <Field label="Model" value={draft.draft?.model} mono />
            <Field label="Condition" value={draft.draft?.condition} />
            <Field label="Suggested location" value={draft.draft?.location} />
            <Field label="Estimated dimensions" value={draft.draft?.estimated_dimensions} mono />
            <Field label="Provenance note" value={draft.draft?.provenance} wide />
            <Field label="Description / materials" value={draft.draft?.notes} wide full />
          </div>

          {/* Valuation block */}
          <div className="border border-[#E0DDD6] bg-white p-5" data-testid="leo-valuation">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                Valuation (Leo&apos;s draft)
              </div>
              <span
                className={`px-2 py-0.5 text-[10px] font-mono-ledger uppercase tracking-[0.2em] ${CONFIDENCE_COLOR[draft.fmv_confidence] || CONFIDENCE_COLOR.unknown}`}
                data-testid="leo-confidence"
              >
                {CONFIDENCE_LABEL[draft.fmv_confidence] || draft.fmv_confidence}
              </span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-[10px] font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58]">FMV</div>
                <div className="text-2xl font-serif-display text-[#0A192F]">
                  {draft.draft?.fmv != null ? fmtMoney(draft.draft.fmv) : "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58]">RCV</div>
                <div className="text-2xl font-serif-display text-[#0A192F]">
                  {draft.draft?.rcv != null ? fmtMoney(draft.draft.rcv) : "—"}
                </div>
              </div>
            </div>
            {draft.fmv_reasoning && (
              <p className="text-xs text-[#5C5F58] mt-3 italic">{draft.fmv_reasoning}</p>
            )}
          </div>

          {/* Clarifying question */}
          {draft.clarifying_question && (
            <div className="border border-[#9C5012]/40 bg-[#9C5012]/5 p-4 flex items-start gap-3" data-testid="leo-question">
              <Warning size={16} weight="regular" className="text-[#9C5012] mt-0.5 shrink-0" />
              <div className="text-sm text-[#0A192F]">
                <span className="font-medium">Leo asks:</span> {draft.clarifying_question}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 items-center pt-2 border-t border-[#E0DDD6]">
            <button
              onClick={acceptDraft}
              data-testid="leo-accept-draft"
              className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-5 py-2.5 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818]"
            >
              <Check size={14} weight="bold" />
              Use this draft
            </button>
            {draft.suggested_comps_query && (
              <button
                onClick={runCompsSearch}
                disabled={refining}
                data-testid="leo-comps-search"
                title={`Search comps for: ${draft.suggested_comps_query}`}
                className="inline-flex items-center gap-2 border border-[#1A3622] text-[#1A3622] px-4 py-2.5 text-sm hover:bg-[#1A3622] hover:text-[#F9F8F6] disabled:opacity-50 transition-colors"
              >
                <Sparkle size={14} weight="bold" />
                {refining ? "Searching comps…" : "Refine value with market comps"}
              </button>
            )}
            <button
              onClick={reset}
              data-testid="leo-reset"
              className="text-xs text-[#5C5F58] hover:text-[#1A3622] underline underline-offset-4 ml-auto"
            >
              Start over with a different photo
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Field({ label, value, mono, full }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className={`border border-[#E0DDD6] bg-white p-3 ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58]">
        {label}
      </div>
      <div className={`mt-1 text-sm text-[#0A192F] whitespace-pre-wrap ${mono ? "font-mono-ledger text-xs" : ""}`}>
        {String(value)}
      </div>
    </div>
  );
}

function prettyCategory(c) {
  return ({
    high_end_gear: "High-End Gear",
    home_component: "Home Component",
    fine_art: "Fine Art & Collectibles",
    vehicle_marine: "Vehicles & Marine",
    other: "Other",
  })[c] || c;
}
